import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { Cupon } from "./Cupon.component";
import { Payment } from "./Payment.component";
import { ICheckoutPage, PriceDetail, Stage } from "./CheckoutMainPage.model";
import { UserInfo } from "./Payment.model";
import { useHistory, useLocation } from "react-router-dom";
import { SubscribeToCheckoutPage } from "../../../apis/Admin/Checkout";
import { Skeleton, Stack } from "@mui/material";
import moment from "moment";
import { GetOrderSummary } from "../../../apis/User/indexv2";
import { CountryCode } from "libphonenumber-js/types.cjs";
import { useUserData } from "./useUserData";
import { useCurrentDate } from "./useCurrentDate";
import { useGetCheckoutPage } from "./useGetCheckoutPage";
import axios from "axios";
import { FormPayment } from "./FormPayment";

const CheckoutMainPage = () => {
  let { state } = useLocation();
  const history = useHistory();
  let publicId: string =
    typeof state === "object" ? (state as any)?.publicId || "" : "";

  const { checkoutPage } = useGetCheckoutPage({ pageId: publicId });
  const { date } = useCurrentDate();
  const {
    isLoading: isLoadingMetadata,
    countryCode,
    ipAddress,
  } = useUserData();

  const [stage, setStage] = useState<Stage>(Stage.CUPON_INFO);
  const [orderId, setOrderId] = useState<string | null | undefined>(undefined);
  const [emailStudent, setEmailStudent] = useState<string | undefined>(undefined);
  const [formToken, setFormToken] = useState<string | undefined>("");
  const [cupon, setCupon] = useState<string | null | undefined>(undefined);
  const [isValidatingCupon, setIsValidatingCupon] = useState<boolean>(false);
  const [priceDetail, setPriceDetail] = useState<PriceDetail>({
    totalPrice: 0,
    totalDiscountPercentPerCode: 0,
    totalDiscountPercentPerTimer: 0,
  });

  const customCountryCode: CountryCode =
    countryCode === "" ? "DE" : (countryCode as CountryCode);

  const handleOnSubmitCupon = useCallback(
    async (cupon: string | null) => {
      try {
        setCupon(cupon);
        const result = await GetOrderSummary({
          publicId,
          code: cupon ?? undefined,
        });
        setOrderId(result.orderId);
        setPriceDetail({
          totalPrice: result.totalPrice,
          totalDiscountPercentPerCode: result.totalDiscountPercentPerCode,
          totalDiscountPercentPerTimer: result.totalDiscountPercentPerTimer,
        });
        setStage(Stage.USER_INFO);
      } catch (error) {}
    },
    [publicId]
  );

  const handleOnSubmitUserInfo = useCallback(
    async (formData: UserInfo) => {
      try {
        const result = await axios.post(
          "https://getpaymentformtoken-5kg2vejuqa-uc.a.run.app",
          {
            orderId: orderId,
            client: {
              firstName: formData.firstName,
              lastName: formData.lastName,
              email: formData.email,
              country: formData.countryCode,
              phoneNumber: formData.phoneNumber,
            },
            student: {
              email: formData.emailStudent,
            },
          }
        );
        if (
          result.status === 200 &&
          typeof result.data?.token === "object" &&
          typeof result.data?.token?.client_secret === "string"
        ) {
          setEmailStudent(formData.emailStudent);
          setFormToken(result.data.token.client_secret);
          setStage(Stage.PAYMENT_FORM);
        }
      } catch (error) {}
    },
    [orderId]
  );

  useEffect(() => {
    if (publicId === "") history.push("/");
  }, [publicId]);

  const totalCodeDiscountsLength = useMemo(() => {
    if (checkoutPage === undefined || checkoutPage === null) return -1;
    return checkoutPage.codes.filter((code) =>
      moment().isBetween(code.activation.dateFrom, code.activation.dateTo)
    ).length;
  }, [checkoutPage, date]);

  useLayoutEffect(() => {
    if (totalCodeDiscountsLength === 0) {
      setStage(Stage.USER_INFO);
    }
  }, [totalCodeDiscountsLength]);

  useLayoutEffect(() => {
    if (stage === Stage.USER_INFO && cupon === undefined) {
      setIsValidatingCupon(true);
      GetOrderSummary({ publicId, code: undefined })
        .then((result) => {
          setOrderId(result.orderId);
          setPriceDetail({
            totalPrice: result.totalPrice,
            totalDiscountPercentPerCode: result.totalDiscountPercentPerCode,
            totalDiscountPercentPerTimer: result.totalDiscountPercentPerTimer,
          });
        })
        .catch(() => {})
        .finally(() => {
          setIsValidatingCupon(false);
        });
    }
  }, [cupon, publicId, stage]);

  if (
    typeof checkoutPage === "undefined" ||
    checkoutPage === null ||
    isValidatingCupon ||
    isLoadingMetadata
  ) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          padding: 24,
          justifyContent: "center",
        }}
      >
        <div style={{ maxWidth: 400, width: "100%" }}>
          <Stack spacing={2}>
            <Skeleton animation="pulse" variant="rounded" height={50} />
            <Skeleton animation="pulse" variant="rounded" height={50} />
            <Skeleton animation="pulse" variant="rounded" height={50} />
            <Skeleton animation="pulse" variant="rounded" height={50} />
            <Skeleton animation="pulse" variant="rounded" height={50} />
          </Stack>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        padding: 24,
        justifyContent: "center",
      }}
    >
      <div style={{ maxWidth: 400 }}>
        {stage === Stage.CUPON_INFO ? (
          <Cupon onSubmit={handleOnSubmitCupon} />
        ) : null}
        {stage === Stage.USER_INFO ? (
          <Payment
            countryCode={customCountryCode}
            priceDetail={priceDetail}
            onSubmit={handleOnSubmitUserInfo}
          />
        ) : null}
        {stage === Stage.PAYMENT_FORM &&
        typeof formToken === "string" &&
        typeof orderId === "string" &&
        typeof checkoutPage?.course?.id === "string" &&
        typeof emailStudent === "string" ? (
          <FormPayment
            emailStudent={emailStudent}
            formToken={formToken}
            orderId={orderId}
            courseId={checkoutPage.course.id}
            publicId={publicId}
          />
        ) : null}
      </div>
    </div>
  );
};

export { CheckoutMainPage };
